import { useMemo, type ReactNode } from 'react';
import { ColorTheme, ThemeProvider as FplThemeProvider } from '@figma/fpl-themes';
import { FF_FIGMA_THEME } from '@/consts/featureFlags';
import { useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';
import { FplMigrationProvider, useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { ThemeModeProvider, useThemeMode } from '@/contexts/ThemeModeContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { createDarkTheme } from '@/UI/theme';
import { CssBaseline, ThemeProvider } from '@/UI/theme.exports';

export function WeaveThemeProvider({
  children,
  isFigmaEmbedOverride,
  disableCssBaseline = false,
}: {
  children: ReactNode;
  isFigmaEmbedOverride?: boolean;
  disableCssBaseline?: boolean;
}) {
  // Mounted as the outermost layer so FF_FPL_MIGRATION is read exactly once here and shared via
  // context with every descendant (see FplMigrationContext), instead of each consumer re-running
  // useFeatureFlag itself. FF_LIGHT_MODE is shared the same way via ThemeModeProvider.
  return (
    <FplMigrationProvider>
      <ThemeModeProvider isFigmaEmbedOverride={isFigmaEmbedOverride}>
        <WeaveThemeProviderContent isFigmaEmbedOverride={isFigmaEmbedOverride} disableCssBaseline={disableCssBaseline}>
          {children}
        </WeaveThemeProviderContent>
      </ThemeModeProvider>
    </FplMigrationProvider>
  );
}

function WeaveThemeProviderContent({
  children,
  isFigmaEmbedOverride,
  disableCssBaseline,
}: {
  children: ReactNode;
  isFigmaEmbedOverride?: boolean;
  disableCssBaseline: boolean;
}) {
  const { value: isFigmaThemeEnabled } = useFeatureFlag(FF_FIGMA_THEME);
  const isFplMigrationEnabled = useFplMigrationFlag();
  // Weave-in-Figma always uses the black/white theme; standalone Weavy keeps the
  // flag (the figma-theme rebrand rollout). Forcing it here keeps the whole
  // embedded app B/W even before the rollout reaches non-employee/anonymous
  // sessions, avoiding a mixed white-buttons/yellow-chrome state. A caller that
  // mounts this provider above FigmaEmbedModeProvider, where the context reads
  // its default false, passes isFigmaEmbedOverride so it builds the same theme
  // CSS vars as the app.
  const contextIsFigmaEmbed = useIsFigmaEmbedMode();
  const isFigmaEmbed = isFigmaEmbedOverride ?? contextIsFigmaEmbed;
  const shouldUseFigmaTheme = isFigmaThemeEnabled === true || isFigmaEmbed;
  const shouldUseFplTypography = isFplMigrationEnabled === true;
  const mode = useThemeMode();
  const theme = useMemo(
    () =>
      createDarkTheme({
        useFigmaColors: shouldUseFigmaTheme,
        useFplTypography: shouldUseFplTypography,
      }),
    [shouldUseFigmaTheme, shouldUseFplTypography],
  );

  // The MUI theme is still dark-only: `createDarkTheme` is migrated surface-by-surface in phase 2
  // of the light-mode plan. Until then, flag-on light mode renders FPL tokens light over a dark MUI
  // baseline — expected mid-migration, and visible in Storybook so the gap is tracked rather than
  // discovered in production. The flag stays off in production until phase 2 lands.
  return (
    <FplThemeProvider>
      <ColorTheme brand="weave" mode={mode}>
        <ThemeProvider theme={theme}>
          {!disableCssBaseline && <CssBaseline />}
          {children}
        </ThemeProvider>
      </ColorTheme>
    </FplThemeProvider>
  );
}
